import React, { useState, useRef, useEffect } from 'react';
import { Lock } from 'lucide-react';
import styles from './form.module.css';

const DEFAULT_CLICK_MARKER_COLOR = '#0ea5e9';
const hexToRgba = (hex, alpha) => {
    if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex)) return `rgba(14,165,233,${alpha})`;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
};

const ClickImageForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true }) => {
    const imageUrl = content?.settings?.imageUrl || '';
    const maxClicks = Number(content?.settings?.maxClicks ?? 1);
    const imageNaturalWidth = content?.settings?.imageNaturalWidth;
    const imageNaturalHeight = content?.settings?.imageNaturalHeight;
    const hasAspectRatio = imageNaturalWidth && imageNaturalHeight;
    const markerColor = content?.settings?.clickMarkerColor || DEFAULT_CLICK_MARKER_COLOR;
    const [clicks, setClicks] = useState(() => userAnswer?.clicks || []);
    const imgRef = useRef(null);

    useEffect(() => {
        setClicks(userAnswer?.clicks || []);
    }, [userAnswer]);

    const hasSubmitted = userAnswer != null;
    const isDisabled = isLocked || (hasSubmitted && !allowUpdateResponse);
    const isUnchanged = hasSubmitted && JSON.stringify(clicks) === JSON.stringify(userAnswer?.clicks || []);
    const submitDisabled = isPreview || isLocked || clicks.length === 0 || (hasSubmitted && (!allowUpdateResponse || isUnchanged));
    const submitLabel = !hasSubmitted ? (isLocked ? 'Voting Closed' : 'Submit') : (!allowUpdateResponse || isUnchanged ? 'Submitted ✓' : 'Update response');

    const handleImageClick = (e) => {
        if (isDisabled) return;
        const rect = imgRef.current.getBoundingClientRect();
        const x = parseFloat(((e.clientX - rect.left) / rect.width * 100).toFixed(2));
        const y = parseFloat(((e.clientY - rect.top) / rect.height * 100).toFixed(2));
        setClicks(prev => {
            const next = [...prev, { x, y }];
            return next.length > maxClicks ? next.slice(-maxClicks) : next;
        });
    };

    const handleSubmit = () => {
        if (submitDisabled) return;
        onSubmit({ clicks });
    };

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content.question || 'Click on the image'}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden="true" /> Voting Locked</span>}
            </div>
            <div className={styles.description}>
                {isDisabled ? 'Response recorded.' : `Click on the image to mark your answer. (${clicks.length}/${maxClicks})`}
            </div>
            <div
                className={`${styles.imageTargetWrap} ${!isDisabled ? styles.clickable : ''}`}
                style={hasAspectRatio ? { aspectRatio: `${imageNaturalWidth} / ${imageNaturalHeight}` } : undefined}
                onClick={handleImageClick}
            >
                <img
                    ref={imgRef}
                    src={imageUrl}
                    alt="Click target"
                    className={styles.imageTargetImg}
                    style={hasAspectRatio ? { height: '100%', maxHeight: 'none' } : undefined}
                    draggable={false}
                />
                {clicks.map((c, i) => (
                    <div
                        key={i}
                        className={styles.clickMarker}
                        style={{
                            left: `${c.x}%`,
                            top: `${c.y}%`,
                            background: markerColor,
                            boxShadow: `0 2px 6px ${hexToRgba(markerColor, 0.5)}`,
                        }}
                    >
                        {i + 1}
                    </div>
                ))}
            </div>
            {clicks.length > 0 && !isDisabled && (
                <button
                    type="button"
                    className={styles.ghostBtn}
                    onClick={() => setClicks([])}
                >
                    Clear clicks
                </button>
            )}
            <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitDisabled}>
                {submitLabel}
            </button>
        </div>
    );
};

export default ClickImageForm;
